import type { CustomFieldDef, Entity, Me } from '$lib/types';
import { route } from './route.svelte';

// A one-shot cross-page signal: the Header's "+ New" dropdown and topbar
// search results both need to switch tabs *and* tell the destination page to
// open a modal (create, or edit a specific record) once it mounts. Kept
// deliberately generic/untyped on `record` — each entity page casts it to
// its own row type when it consumes the signal.
type PageSignal =
  | { tab: string; mode: 'create'; prefill?: Record<string, unknown> }
  | { tab: string; mode: 'edit'; record: unknown };

// Bootstrapped once after login; holds the current user, their accessible
// verticals, and custom field definitions.
class SessionState {
  me = $state<Me | null>(null);
  private pageSignal = $state<(PageSignal & { token: number }) | null>(null);

  readonly isAdmin = $derived(this.me?.user.role === 'admin');

  // The vertical switcher's selection. The URL's `?vertical=` query param
  // (see route.svelte.ts) is the single source of truth — this is a
  // read-only derived view over it, with the same fallback the old
  // eagerly-written default used: a single/first-of-multiple vertical for
  // members, 'all' for admins. Writers (Header's dropdown, dashboard scope
  // chips) go through `route.push`/`route.replace`, not this field, so
  // every page — which all already read `session.selectedVertical` — reacts
  // identically whether the change came from the dropdown or a hand-edited
  // hash, and the selection survives reload.
  readonly selectedVertical = $derived.by((): number | 'all' => {
    const raw = route.current.vertical;
    // Same default a fresh login used to write eagerly: a member's sole
    // vertical, or their first if they have several non-admin grants —
    // otherwise (admins, or no verticals) 'all'.
    const fallback = (): number | 'all' => {
      const n = this.me?.verticals.length ?? 0;
      if (n === 1) return this.me!.verticals[0].id;
      if (n > 1 && this.me!.user.role !== 'admin') return this.me!.verticals[0].id;
      return 'all';
    };
    if (raw === undefined) return fallback();
    if (raw === 'all') return this.isAdmin ? 'all' : fallback();
    const id = Number(raw);
    if (Number.isFinite(id) && this.me?.verticals.some((v) => v.id === id)) return id;
    return fallback();
  });

  setMe(me: Me) {
    this.me = me;
  }

  // `me.custom_fields` is the union of every accessible vertical's defs
  // (fetched once at bootstrap) — callers rendering a form/detail for one
  // specific vertical must filter down to it, or fields from other
  // verticals bleed in (e.g. a Hotels form showing another vertical's
  // "Ideal Customer Profile" checkbox). `verticalId` undefined (no vertical
  // picked yet) yields no fields rather than guessing.
  customFieldsFor(entity: Entity, verticalId: number | undefined): CustomFieldDef[] {
    if (verticalId === undefined) return [];
    return (this.me?.custom_fields[entity] ?? []).filter((f) => f.vertical_id === verticalId);
  }

  clear() {
    this.me = null;
    this.pageSignal = null;
  }

  requestCreate(tab: string, prefill?: Record<string, unknown>) {
    this.pageSignal = { tab, mode: 'create', prefill, token: Date.now() + Math.random() };
  }

  requestEdit(tab: string, record: unknown) {
    this.pageSignal = { tab, mode: 'edit', record, token: Date.now() + Math.random() };
  }

  // Consumes (clears) the pending signal for `tab` if there is one. Pages
  // call this from a $effect so it fires both on mount (tab just switched
  // in) and on later signals while already active.
  consumeSignal(tab: string): PageSignal | null {
    if (this.pageSignal && this.pageSignal.tab === tab) {
      const { token: _token, ...rest } = this.pageSignal;
      this.pageSignal = null;
      return rest;
    }
    return null;
  }
}

export const session = new SessionState();
